import { identity } from 'lodash-es';
import { THIRD_PARTY_AUTH_SERVICE_BASEURL } from 'config';
import ServiceFactory from 'services/ServiceFactory';
import {
  LOGIN_PATH,
  MY_GOOGLE_DRIVE_FILE_DETAIL_PATH,
  MY_GOOGLE_DRIVE_FILES_PATH,
  MY_THIRD_PARTY_AUTH_PATH,
  MY_ZOOM_RECORDING_DETAIL_PATH,
  MY_ZOOM_RECORDINGS_PATH,
} from './constants';
import {
  CreateAuthUrlResponse,
  GetGoogleDriveFileDetailArgs,
  GetGoogleDriveFilesArgs,
  GetZoomRecordingArgs,
  GetZoomRecordingDetailArgs,
  GoogleDriveFileDetail,
  GoogleDriveFilesResult,
  ThirdPartyAuthRequest,
  ThirdPartyLoginRequest,
  ZoomRecordingDetail,
  ZoomRecordingsResult,
} from './types';

const serviceFactory = new ServiceFactory(THIRD_PARTY_AUTH_SERVICE_BASEURL);

/**
 * Retrieve third party social auth for a given user.
 *
 * This endpoint tries to use the refresh token to get an id token and access
 * token, which will be cached
 */
export const getMyThirdPartyAuth = serviceFactory.createGetEndpoint(
  MY_THIRD_PARTY_AUTH_PATH,
)({
  queryParams({ forceLogin, ...args }: ThirdPartyAuthRequest) {
    return {
      // if forceLogin isn't explicitly set to false, API returns
      // "404: no refresh tokens found"
      forceLogin: !!forceLogin,
      ...args,
    };
  },
  withCredentials: true,
});

/**
 * Obtain the recording list associated with the user from Zoom
 *
 * The response retains the meeting/recording structure from Zoom. All recordings
 * are found under a meeting. It is up to the client to figure out how to display
 * the recordings under the meetings. Recording for a meeting can be empty since
 * we post process and filter out any recordings that doesn't meet the condition
 * for being playable (unsupported media or status is not ready).
 */
export const getMyZoomRecordings = serviceFactory.createPostEndpoint(
  MY_ZOOM_RECORDINGS_PATH,
)<GetZoomRecordingArgs, ZoomRecordingsResult>({
  data: ['accessToken', 'providerUserId'],
  queryParams: ['mediaType', 'pageSize', 'pageToken'],
});

/**
 * Obtain the meeting's recording detail
 */
export const getMyZoomRecordingDetail = serviceFactory.createPostEndpoint(
  MY_ZOOM_RECORDING_DETAIL_PATH,
)<GetZoomRecordingDetailArgs, ZoomRecordingDetail>({
  data: ['accessToken', 'providerUserId'],
});

/**
 * Obtain the file list associated with the user from Google Drive
 *
 * Automatically filters for downloadable audio and video files. The result is
 * not expected to match the input pageSize. This is due to the fact that Google
 * Drive api does not support filter by downloable assets. So we do some postprocessing
 * to filter out assets that are not downloadable. It's possible that you can get zero
 * results even if a pageSize of a non-zero value was requested.
 */
export const getMyGoogleDriveFiles = serviceFactory.createPostEndpoint(
  MY_GOOGLE_DRIVE_FILES_PATH,
)<GetGoogleDriveFilesArgs, GoogleDriveFilesResult>({
  data: ['accessToken', 'providerUserId'],
  queryParams: ['mediaType', 'pageSize', 'pageToken'],
});

/**
 * Obtain the file detail
 */
export const getMyGoogleDriveFileDetail = serviceFactory.createPostEndpoint(
  MY_GOOGLE_DRIVE_FILE_DETAIL_PATH,
)<GetGoogleDriveFileDetailArgs, GoogleDriveFileDetail>({
  data: ['accessToken', 'providerUserId'],
});

/**
 * Generate a third party auth url for logging in to Headliner
 */
export const generateAuthUrl = serviceFactory.createGetEndpoint(LOGIN_PATH)<
  ThirdPartyLoginRequest,
  CreateAuthUrlResponse
>({
  pathVariables: identity,
  withCredentials: true,
});
